import * as Sentry from "@sentry/react-router";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface MapErrorBoundaryProps {
	readonly children: ReactNode;
}

interface MapErrorBoundaryState {
	hasError: boolean;
}

/**
 * Error boundary that catches rendering errors and chunk load failures
 * in the pharmacy map subtree. Reports to Sentry and renders a
 * recoverable fallback UI.
 */
export class MapErrorBoundary extends Component<
	MapErrorBoundaryProps,
	MapErrorBoundaryState
> {
	constructor(props: MapErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): MapErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		Sentry.captureException(error, {
			extra: {
				componentStack: errorInfo.componentStack,
				feature: "pharmacy-map",
			},
		});
	}

	private handleRetry = (): void => {
		this.setState({ hasError: false });
	};

	render(): ReactNode {
		if (this.state.hasError) {
			return (
				<div
					className="flex flex-col items-center justify-center w-full h-full min-h-[250px] bg-gray-50 text-gray-500 gap-2"
					data-testid="pharmacy-map-boundary-error"
				>
					<p className="text-body-bold">Unable to load the map</p>
					<button
						type="button"
						className="text-body text-blue-600 hover:underline"
						onClick={this.handleRetry}
					>
						Try again
					</button>
				</div>
			);
		}
		return this.props.children;
	}
}
